import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { RazorToggleSlide } from "../../../../components/razor-reuse";
import { Button } from "../../../../components/custom/custom-button";
import PaginationFragment from "../../../../components/fragment/pagination/PaginationFragment";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import type { AppDispatch } from "../../../../redux/store";
import RootState from "../../../../redux/type";
import {
  deleteExistingRateAlert,
  getComparisonCurrencyPairOptions,
  getRateAlerts,
  setRateAlertActiveStatus,
} from "../../../../redux/rateIntelligence";
import { formatAlertCondition, RateAlertRule } from "../types";
import { hasRateIntelligenceRole } from "../../../../utils/roles";
import AlertStatusBadge from "./AlertStatusBadge";
import CreateAlertDrawer from "./CreateAlertDrawer";

const ALERTS_PER_PAGE = 10;

export default function AlertsTab() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { alert_rules, alerts_meta, loading_alerts } = useSelector(
    (state: RootState) => state.rate_intelligence,
  );
  const hasRole = hasRateIntelligenceRole(user);
  const canCreate = hasRole && !!user?.permissions?.includes("create_rate_alert");
  const canEdit = hasRole && !!user?.permissions?.includes("edit_rate_alert");
  const canDelete = hasRole && !!user?.permissions?.includes("delete_rate_alert");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RateAlertRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<RateAlertRule | null>(null);
  const [page, setPage] = useState(1);

  const refetchAlerts = () => {
    dispatch(getRateAlerts({ per_page: ALERTS_PER_PAGE, page }) as any);
  };

  useEffect(() => {
    refetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page]);

  // The Create/Edit drawer's currency-pair multi-select is sourced from the
  // Comparison tab's live dataset — fetch it here too in case this tab is
  // opened without ever visiting Comparison first. Only needed when the
  // user can open the drawer.
  useEffect(() => {
    if (canCreate || canEdit) {
      dispatch(getComparisonCurrencyPairOptions() as any);
    }
  }, [dispatch, canCreate, canEdit]);

  const rules = alert_rules || [];

  const handleCreateClick = () => {
    if (!canCreate) return;
    setEditingRule(null);
    setDrawerOpen(true);
  };

  const handleEditClick = (rule: RateAlertRule) => {
    if (!canEdit) return;
    setEditingRule(rule);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (saved?: boolean) => {
    setDrawerOpen(false);
    setEditingRule(null);
    if (saved) refetchAlerts();
  };

  const handleToggle = async (rule: RateAlertRule) => {
    if (!canEdit) return;
    try {
      await dispatch(
        setRateAlertActiveStatus({ id: rule.id, active: !rule.is_active }) as any,
      ).unwrap();
      refetchAlerts();
    } catch (error) {
      console.error("Failed to update alert status:", error);
    }
  };

  const handleDeleteClick = (rule: RateAlertRule) => {
    if (!canDelete) return;
    setDeletingRule(rule);
  };

  const confirmDelete = async () => {
    if (!deletingRule || !canDelete) return;
    try {
      await dispatch(deleteExistingRateAlert(deletingRule.id) as any).unwrap();
      setDeletingRule(null);
      refetchAlerts();
    } catch (error) {
      console.error("Failed to delete alert:", error);
    }
  };

  return (
    <div className="mt-[16px]">
      {canCreate && (
        <div className="flex justify-end">
          <Button onClick={handleCreateClick}>
            <Plus className="size-[16px]" />
            Create alert
          </Button>
        </div>
      )}

      <div className="mt-[16px] bg-white border border-[#E4E4E7] rounded-[8px] overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F8F9FF] text-[13px] font-medium uppercase text-[#191C1F]">
              <th className="px-[20px] py-[12px]">Rule</th>
              <th className="px-[20px] py-[12px]">Condition</th>
              <th className="px-[20px] py-[12px]">Enable</th>
              <th className="px-[20px] py-[12px]">Status</th>
              <th className="px-[20px] py-[12px] text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading_alerts ? (
              <tr>
                <td colSpan={5} className="px-[20px] py-[32px] text-center text-[13px] text-[#A1A1AA]">
                  Loading alerts…
                </td>
              </tr>
            ) : rules.length < 1 ? (
              <tr>
                <td colSpan={5} className="px-[20px] py-[32px] text-center text-[13px] text-[#A1A1AA]">
                  No alerts set up yet
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.id} className="border-t border-[#F0F0F0]">
                  <td className="px-[20px] py-[16px] text-[14px] font-bold text-[#1E1E1E]">
                    {rule.name || "Unnamed alert"}
                  </td>
                  <td className="px-[20px] py-[16px] text-[14px] text-[#1E1E1E]">
                    {formatAlertCondition(rule)}
                  </td>
                  <td className="px-[20px] py-[16px]">
                    <RazorToggleSlide
                      id={rule.id}
                      color="black-light"
                      checked={rule.is_active}
                      disabled={!canEdit}
                      onChange={() => handleToggle(rule)}
                    />
                  </td>
                  <td className="px-[20px] py-[16px]">
                    <AlertStatusBadge status={rule.is_active ? "active" : "paused"} />
                  </td>
                  <td className="px-[20px] py-[16px] text-right">
                    <div className="flex items-center justify-end gap-[12px]">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleEditClick(rule)}
                          className="text-[#A1A1AA] hover:text-[#734792]"
                        >
                          <Pencil className="size-[14px]" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(rule)}
                          className="text-[#A1A1AA] hover:text-[#DC2626]"
                        >
                          <Trash2 className="size-[14px]" />
                        </button>
                      )}
                      {!canEdit && !canDelete && (
                        <span className="text-[13px] text-[#A1A1AA]">--</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="border-t border-[#E4E4E7]">
          <PaginationFragment
            currentPage={alerts_meta?.current_page || page}
            perPage={alerts_meta?.per_page || ALERTS_PER_PAGE}
            totalPage={alerts_meta?.total || 0}
            hidePagination={loading_alerts}
            onPageChange={(num) => setPage(num)}
          />
        </div>
      </div>

      <CreateAlertDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        editingRule={editingRule}
      />

      <ReuseModal
        title="Delete alert?"
        visible={!!deletingRule}
        onClose={() => setDeletingRule(null)}
        onCancel={() => setDeletingRule(null)}
        onBtnClick={confirmDelete}
        btnLabel="Delete"
        deleteType
        deleteText={`Are you sure you want to delete "${
          deletingRule?.name || "this alert"
        }"? This can't be undone.`}
      />
    </div>
  );
}
